/**
 * A prisoner's way out (10 §2, mission 32). The generator promises a parachutist a quiet way IN by keeping
 * every posted man's eyes off one stretch of ground (quietSide.ts); a man who starts in a cell needs the same
 * promise about his way OUT - from the cell, through the cut in the wire, to his pods, and on to the pickup.
 *
 * The cells of that way are handed to `ctx.quiet.exposed`, which `planQuietApproach` keeps (it only adds its
 * own), so nothing the budget buys afterwards - sentry, gun or lamp - may overlook them. The camp's own
 * guards are posted before that machinery exists, so they are checked here against the same model of sight.
 */

import { collidersFor } from '../../sim/props';
import type { EnemyKind, SpawnDef } from '../../sim/types';
import { NO_STAND } from '../builder';
import { FLAG } from '../grid';
import type { Point } from '../types';
import { type GarrisonView, watcherOf } from '../validate/quiet/model';
import { SightIndex, trenchCells } from '../validate/quiet/sight';
import type { GoalCtx } from './types';

/** Cells of a polyline, three wide, that are not shut: reserved as they are collected, so nothing is built on them. */
export function reserveWay(g: GoalCtx, points: readonly Point[]): number[] {
  const { width, height } = g.map.mask;
  const cells = new Set<number>();
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!a || !b) continue;
    const steps = Math.max(1, Math.ceil(Math.abs(b.x - a.x) + Math.abs(b.z - a.z)));
    for (let s = 0; s <= steps; s++) {
      const x = Math.floor(a.x + ((b.x - a.x) * s) / steps);
      const z = Math.floor(a.z + ((b.z - a.z) * s) / steps);
      for (const [dx, dz] of [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const [cx, cz] = [x + dx, z + dz];
        if (cx < 0 || cz < 0 || cx >= width || cz >= height) continue;
        if ((g.map.mask.at(cx + 0.5, cz + 0.5) & (FLAG.solid | FLAG.mine | FLAG.water)) !== 0) continue;
        cells.add(cz * width + cx);
      }
    }
  }
  g.map.mask.line(points.map((p) => [p.x, p.z] as const), 1.2, FLAG.keep | FLAG.path);
  return [...cells];
}

/** The validator's model of sight over what stands on the map right now. */
export function sightNow(g: GoalCtx): { sight: SightIndex; view: GarrisonView } {
  const { width, height } = g.map.mask;
  const sight = new SightIndex(g.map.props.flatMap((p) => collidersFor(p)), width, height, trenchCells(g.map.trenches, width, height));
  return { sight, view: { timeOfDay: g.ctx.params.timeOfDay, props: g.map.props, inTrench: (x, z) => sight.inTrench(x, z) } };
}

/**
 * Post a guard who does not overlook the way out: as placed, else turned about, else not at all.
 * Returns him, or null when no facing keeps his eyes off it (or there is nowhere to stand).
 */
export function blindGuard(
  g: GoalCtx, eyes: ReturnType<typeof sightNow>, way: readonly number[], x: number, z: number, facing: number, kind: EnemyKind,
): SpawnDef | null {
  const spot = g.map.nearestOpen(x, z, 5, NO_STAND | FLAG.keep);
  if (!spot) return null;
  for (const turn of [0, 3.1416, 1.5708, -1.5708]) {
    const man: SpawnDef = { kind, x: spot[0], z: spot[1], facing: facing + turn };
    const watcher = watcherOf(eyes.view, man);
    if (watcher && eyes.sight.seesAny(watcher, way)) continue;
    return g.map.spawn(man);
  }
  return null;
}
